/* === Produktdetailseite: Inhalt in seine Zone einpassen ===
   Die Zonen der rechten Spalte (und der Titel darüber) haben in base.css eine FESTE Höhe in
   Rasterzellen, damit der Kaufbereich auf jedem Produkt an derselben Stelle sitzt. Feste Höhe
   heißt aber auch: zu viel Inhalt würde abgeschnitten. Genau das verhindert dieses Skript.

   Reihenfolge der Eskalation, bewusst in dieser Abstufung:
     1. Laufweite (letter-spacing) und Zeilenabstand (line-height) enger ziehen.
     2. Erst wenn das nicht reicht, die Schriftgröße verringern.
   Zusammenziehen fällt beim Lesen kaum auf; eine kleinere Schrift schon. Deshalb wird sie
   so spät wie möglich angefasst.

   Angriffspunkte sind drei CSS-Variablen, die hier auf dem ZONEN-Element gesetzt werden und
   deren Wirkung in base.css definiert ist (Abschnitt "Angriffspunkte für die automatische
   Anpassung"): --zone-ls, --zone-lh, --zone-fs. */
(() => {
  const ZONES = [
    '.product__title-top',
    '.product__price-wrapper',
    '.product__bio-info',
    '.product__info-block'
  ];

  // Nur Desktop. Unterhalb 750px haben die Zonen in base.css gar keine feste Höhe (dort ist
  // eine Rasterzelle nur ~34px breit), es gibt also nichts einzupassen.
  const DESKTOP = window.matchMedia('(min-width: 750px)');

  // Stufenleiter. Stufe 0 = unverändert. ls/lh werden zuerst ausgereizt, fs bleibt dabei 1.
  const STEPS = [
    { ls: 'normal',   lh: 1,    fs: 1    },
    { ls: '-0.005em', lh: 0.97, fs: 1    },
    { ls: '-0.01em',  lh: 0.94, fs: 1    },
    { ls: '-0.02em',  lh: 0.90, fs: 1    },
    { ls: '-0.03em',  lh: 0.86, fs: 1    },
    { ls: '-0.03em',  lh: 0.86, fs: 0.95 },
    { ls: '-0.03em',  lh: 0.86, fs: 0.90 },
    { ls: '-0.03em',  lh: 0.84, fs: 0.85 },
    { ls: '-0.03em',  lh: 0.82, fs: 0.80 },
    { ls: '-0.03em',  lh: 0.80, fs: 0.72 }
  ];

  const num = (v) => parseFloat(v) || 0;

  // Höhe des tatsächlichen Inhalts. Bewusst NICHT scrollHeight: der Titel ist ein
  // Flex-Container mit align-items: center, dort läuft zu viel Inhalt nach OBEN UND unten
  // über — scrollHeight misst aber nur den Überlauf nach unten und meldete "passt".
  const contentHeight = (el) => {
    let h = 0;
    for (const child of el.children) {
      const cs = getComputedStyle(child);
      if (cs.position === 'absolute' || cs.position === 'fixed') continue;
      h += child.offsetHeight + num(cs.marginTop) + num(cs.marginBottom);
    }
    return h;
  };

  const availableHeight = (el) => {
    const cs = getComputedStyle(el);
    return el.clientHeight - num(cs.paddingTop) - num(cs.paddingBottom);
  };

  const applyStep = (el, step) => {
    el.style.setProperty('--zone-ls', step.ls);
    el.style.setProperty('--zone-lh', step.lh);
    el.style.setProperty('--zone-fs', step.fs);
  };

  const clearStep = (el) => {
    el.style.removeProperty('--zone-ls');
    el.style.removeProperty('--zone-lh');
    el.style.removeProperty('--zone-fs');
  };

  const fit = (el) => {
    if (!DESKTOP.matches) {
      clearStep(el);
      return;
    }
    // Immer bei Stufe 0 beginnen, nicht beim zuletzt gesetzten Zustand: sonst bliebe eine
    // einmal verkleinerte Zone auch dann klein, wenn der Grund dafür weg ist (Variantenwechsel,
    // Fensterbreite, geänderte Rasterzelle).
    for (const step of STEPS) {
      applyStep(el, step);
      // Toleranz 1px gegen Subpixel-Rundung — ohne sie würde eine exakt passende Zone
      // grundlos eine Stufe enger gesetzt.
      if (contentHeight(el) <= availableHeight(el) + 1) return;
    }
    // Alle Stufen ausgereizt: die engste bleibt stehen. Der Rest wird von overflow: hidden
    // abgeschnitten — dann ist der zugehörige --pdp-zone-* Regler in base.css zu klein.
  };

  // === Variantenspalte: Oberkante auf die des Bio-Logos (18.09.2026) ===
  // Die Auswahl steht rechts in einer eigenen Spalte (section-main-product.css,
  // "VARIANTENAUSWAHL ALS EIGENE SPALTE"). Ihre Oberkante soll auf der Oberkante des
  // EU-Bio-Logos liegen.
  //
  // Warum gemessen und nicht gerechnet: Die Bio-Zone ist ein Flex-Kasten fester Hoehe mit
  // justify-content: center. Ihr Inhalt sitzt also mittig, und wo die Logo-Oberkante landet,
  // haengt an der Hoehe dieses Inhalts - Logo allein, Logo + Kontrollnummer, Logo +
  // Kontrollnummer + Herkunft. Gemessen lag das Label dadurch 12px ueber dem Logo.
  //
  // Hat ein Produkt kein Logo (konventionelle Ware), gilt die Oberkante der Bio-Zone - das
  // ist die Stelle, an der das Logo staende.
  const variantenOben = () => {
    const container = document.querySelector('.product__info-container');
    if (!container) return;
    if (!DESKTOP.matches) {
      container.style.removeProperty('--pdp-varianten-oben');
      return;
    }
    const vs = container.querySelector('variant-selects');
    const ziel = container.querySelector('.product__bio-logo') || container.querySelector('.product__bio-info');
    if (!vs || !ziel) {
      container.style.removeProperty('--pdp-varianten-oben');
      return;
    }
    const zb = ziel.getBoundingClientRect();
    if (zb.height < 1 && ziel.classList.contains('product__bio-logo')) return;
    // Ohne den eigenen Versatz rechnen: Der aktuelle margin-top steckt in der Lage von vs,
    // nicht in der des Ziels - gemessen wird die Lage des ZIELS gegen den Container, beide
    // ohne Zutun der Auswahl. Damit ist der Wert nach einem Durchgang stabil.
    const abstand = Math.round(zb.top - container.getBoundingClientRect().top);
    const alt = parseFloat(container.style.getPropertyValue('--pdp-varianten-oben')) || 0;
    if (Math.abs(abstand - alt) > 0.5) {
      container.style.setProperty('--pdp-varianten-oben', abstand + 'px');
    }
  };

  const fitAll = () => {
    variantenOben();
    for (const selector of ZONES) {
      document.querySelectorAll(selector).forEach(fit);
    }
  };

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      fitAll();
    });
  };

  schedule();

  // Webfonts ändern die Textmaße deutlich — vor dem Laden gemessen wäre jede Stufe geraten.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);

  window.addEventListener('resize', schedule);
  DESKTOP.addEventListener('change', schedule);

  // Die Zonenhöhen hängen an --cell, das grid-background.js aus der Inhaltsbreite misst und
  // erst nach dem ersten Zeichnen setzt. Ein ResizeObserver auf den Zonen fängt das ebenso ab
  // wie einen Variantenwechsel, der Preis- oder Grundpreiszeile austauscht.
  if (window.ResizeObserver) {
    const ro = new ResizeObserver(schedule);
    for (const selector of ZONES) {
      document.querySelectorAll(selector).forEach((el) => ro.observe(el));
    }
  }

  // Variantenwechsel ersetzt den Preisblock per Section Rendering — danach neu einpassen.
  document.addEventListener('change', (event) => {
    if (event.target.closest && event.target.closest('variant-selects, .product-form__input')) {
      setTimeout(schedule, 250);
    }
  });
})();
